/**
 * ProfileSection Component
 * User profile editing with display name, email status, and follower counts
 */

import type { User } from '@contexts/AuthContext';
import { useAuth } from '@contexts/AuthContext';
import { useUserProfile } from '@hooks/useUserProfile';
import { AUTH_OPERATION_FALLBACKS } from '@parlay-picker/shared/authMessages';
import {
  cleanSocialHandle,
  SOCIAL_PLATFORMS,
  type SocialLinks,
  type SocialPlatformKey,
} from '@parlay-picker/shared/socialLinks';
import { safeParseJson } from '@utils/api';
import {
  AlertTriangle,
  Instagram,
  Mail,
  MessageCircle,
  Music2,
  Pencil,
  Target,
  Twitch,
  Twitter,
  X,
  Youtube,
} from 'lucide-react';
import type { ReactElement } from 'react';
import { useCallback, useEffect, useState } from 'react';

const SOCIAL_ICON_MAP: Record<string, typeof Twitter> = {
  Twitter,
  Instagram,
  Music2,
  Youtube,
  MessageCircle,
  Twitch,
  Target,
};

interface ProfileSectionProps {
  // User from top-level auth (already available in parent)
  user: User | null;

  // Handlers
  onSaveProfile: (profile: {
    displayName?: string;
    firstName?: string;
    lastName?: string;
  }) => Promise<void>;
  onRequestEmailChange: (newEmail: string, password: string) => Promise<unknown>;
  onCancelEmailChange: () => Promise<void>;
}

export function ProfileSection({
  user,
  onSaveProfile,
  onRequestEmailChange,
  onCancelEmailChange,
}: ProfileSectionProps): ReactElement {
  const { authFetch } = useAuth();
  const { profile } = useUserProfile({ userId: user?.id ?? '' });

  const [isEditingProfile, setIsEditingProfile] = useState(false);
  const [displayNameInput, setDisplayNameInput] = useState('');
  const [firstNameInput, setFirstNameInput] = useState('');
  const [lastNameInput, setLastNameInput] = useState('');
  const [savingProfile, setSavingProfile] = useState(false);

  // Email change state
  const [isEditingEmail, setIsEditingEmail] = useState(false);
  const [newEmailInput, setNewEmailInput] = useState('');
  const [emailPasswordInput, setEmailPasswordInput] = useState('');
  const [savingEmail, setSavingEmail] = useState(false);
  const [emailChangeError, setEmailChangeError] = useState<string | null>(null);
  const [emailChangeSuccess, setEmailChangeSuccess] = useState(false);

  // Social links state
  const [socialLinksInput, setSocialLinksInput] = useState<SocialLinks>({});
  const [savingSocial, setSavingSocial] = useState(false);
  const [socialLinksError, setSocialLinksError] = useState<string | null>(null);
  const [socialLoaded, setSocialLoaded] = useState(false);

  // Load social links from profile
  useEffect(() => {
    if (profile?.user && !socialLoaded) {
      const existing = (profile.user as { socialLinks?: SocialLinks }).socialLinks;
      if (existing) {
        setSocialLinksInput(existing);
      }
      setSocialLoaded(true);
    }
  }, [profile, socialLoaded]);

  const handleSaveSocialLinks = useCallback(async () => {
    setSavingSocial(true);
    setSocialLinksError(null);
    try {
      const cleaned: SocialLinks = {};
      for (const platform of SOCIAL_PLATFORMS) {
        const key = platform.key as SocialPlatformKey;
        const raw = socialLinksInput[key];
        if (raw) {
          cleaned[key] = cleanSocialHandle(key, raw);
        }
      }
      const res = await authFetch('/api/auth/social-links', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ socialLinks: cleaned }),
      });
      if (!res.ok) {
        const data = await safeParseJson<{ error?: string }>(res);
        setSocialLinksError(data.error || AUTH_OPERATION_FALLBACKS.saveSocialLinks);
      }
    } catch {
      setSocialLinksError(AUTH_OPERATION_FALLBACKS.saveSocialLinks);
    }
    setSavingSocial(false);
  }, [socialLinksInput, authFetch]);

  const handleEmailChange = async (): Promise<void> => {
    if (!newEmailInput.trim() || !emailPasswordInput) return;
    setSavingEmail(true);
    setEmailChangeError(null);
    try {
      await onRequestEmailChange(newEmailInput.trim(), emailPasswordInput);
      setEmailChangeSuccess(true);
      setIsEditingEmail(false);
      setNewEmailInput('');
      setEmailPasswordInput('');
    } catch (err) {
      setEmailChangeError(err instanceof Error ? err.message : 'Failed to request email change');
    }
    setSavingEmail(false);
  };

  const handleCancelPendingEmail = async (): Promise<void> => {
    setEmailChangeError(null);
    try {
      await onCancelEmailChange();
    } catch (err) {
      setEmailChangeError(
        err instanceof Error ? err.message : AUTH_OPERATION_FALLBACKS.cancelEmailChange,
      );
    }
  };

  const handleSave = async (): Promise<void> => {
    if (!firstNameInput.trim() || !lastNameInput.trim()) return;
    setSavingProfile(true);
    try {
      await onSaveProfile({
        displayName: displayNameInput || undefined,
        firstName: firstNameInput.trim(),
        lastName: lastNameInput.trim(),
      });
      setIsEditingProfile(false);
    } catch {
      // Error handled by parent
    }
    setSavingProfile(false);
  };

  return (
    <>
      <section data-testid="settings-profile-section" className="mb-6">
        <div className="flex items-center justify-between mb-2">
          <h3 className="text-lg font-medium text-theme-text">Profile</h3>
          {!isEditingProfile && (
            <button
              type="button"
              onClick={() => {
                setDisplayNameInput(user?.displayName ?? '');
                setFirstNameInput(user?.firstName ?? '');
                setLastNameInput(user?.lastName ?? '');
                setIsEditingProfile(true);
              }}
              className="text-sm text-theme-primary hover:text-theme-primary-hover"
            >
              Edit
            </button>
          )}
        </div>
        <div className="bg-theme-surface rounded-lg p-4 border border-theme-border">
          <div className="flex items-center gap-4 mb-4">
            <div className="w-16 h-16 rounded-full bg-theme-primary-fill flex items-center justify-center text-theme-on-primary-fill text-2xl font-medium">
              {user?.displayName[0].toUpperCase()}
            </div>
            <div className="flex-1">
              {isEditingProfile ? (
                <div className="space-y-3">
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <label className="block text-xs text-theme-text-tertiary mb-1">
                        First Name
                      </label>
                      <input
                        type="text"
                        value={firstNameInput}
                        onChange={(e) => setFirstNameInput(e.target.value)}
                        placeholder="First name"
                        className="w-full px-3 py-2 bg-theme-bg border border-theme-border rounded text-theme-text text-sm focus:outline-none focus:border-theme-primary"
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-theme-text-tertiary mb-1">
                        Last Name
                      </label>
                      <input
                        type="text"
                        value={lastNameInput}
                        onChange={(e) => setLastNameInput(e.target.value)}
                        placeholder="Last name"
                        className="w-full px-3 py-2 bg-theme-bg border border-theme-border rounded text-theme-text text-sm focus:outline-none focus:border-theme-primary"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-xs text-theme-text-tertiary mb-1">
                      Display Name
                    </label>
                    <input
                      type="text"
                      value={displayNameInput}
                      onChange={(e) => setDisplayNameInput(e.target.value)}
                      placeholder="Enter display name"
                      className="w-full px-3 py-2 bg-theme-bg border border-theme-border rounded text-theme-text text-sm focus:outline-none focus:border-theme-primary"
                    />
                  </div>
                  <div className="flex gap-2">
                    <button
                      type="button"
                      onClick={handleSave}
                      disabled={savingProfile || !firstNameInput.trim() || !lastNameInput.trim()}
                      className="px-3 py-1.5 bg-theme-primary-fill hover:bg-theme-primary-fill-hover disabled:bg-theme-surface disabled:text-theme-text-tertiary disabled:opacity-50 text-theme-on-primary-fill text-sm font-medium rounded transition-colors"
                    >
                      {savingProfile ? 'Saving...' : 'Save'}
                    </button>
                    <button
                      type="button"
                      onClick={() => setIsEditingProfile(false)}
                      disabled={savingProfile}
                      className="px-3 py-1.5 bg-theme-bg hover:bg-theme-border text-theme-text-secondary text-sm font-medium rounded transition-colors"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              ) : (
                <>
                  <p className="text-theme-text font-medium text-lg">{user?.displayName}</p>
                  <div className="grid grid-cols-2 gap-2 mt-2">
                    <div>
                      <p className="text-xs text-theme-text-tertiary">First Name</p>
                      <p className="text-sm text-theme-text-secondary">{user?.firstName || '—'}</p>
                    </div>
                    <div>
                      <p className="text-xs text-theme-text-tertiary">Last Name</p>
                      <p className="text-sm text-theme-text-secondary">{user?.lastName || '—'}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 mt-2">
                    <p className="text-theme-text-tertiary text-xs">{user?.email}</p>
                    {!isEditingEmail && (
                      <button
                        type="button"
                        onClick={() => {
                          setIsEditingEmail(true);
                          setEmailChangeSuccess(false);
                          setEmailChangeError(null);
                        }}
                        className="text-theme-text-tertiary hover:text-theme-primary transition-colors"
                        title="Change email"
                      >
                        <Pencil className="w-3 h-3" />
                      </button>
                    )}
                  </div>

                  {/* Pending email banner */}
                  {user?.pendingEmail && !isEditingEmail && (
                    <div className="flex items-center gap-2 mt-2 px-2.5 py-1.5 bg-theme-warning/10 border border-theme-warning/20 rounded text-xs">
                      <Mail className="w-3.5 h-3.5 text-theme-warning shrink-0" />
                      <span className="text-theme-warning">
                        Pending: {user.pendingEmail} — check your inbox
                      </span>
                      <button
                        type="button"
                        onClick={handleCancelPendingEmail}
                        className="ml-auto text-theme-text-tertiary hover:text-theme-danger transition-colors"
                        title="Cancel email change"
                      >
                        <X className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  )}
                  {emailChangeError && !isEditingEmail && (
                    <div className="flex items-center gap-1.5 mt-2 text-xs text-theme-danger">
                      <AlertTriangle className="w-3.5 h-3.5" />
                      <span>{emailChangeError}</span>
                    </div>
                  )}

                  {/* Email change success */}
                  {emailChangeSuccess && !user?.pendingEmail && (
                    <p className="text-xs text-theme-success mt-2">
                      Verification email sent — check your inbox.
                    </p>
                  )}

                  {/* Email change form */}
                  {isEditingEmail && (
                    <div className="mt-3 space-y-2">
                      <div>
                        <label className="block text-xs text-theme-text-tertiary mb-1">
                          New Email
                        </label>
                        <input
                          type="email"
                          value={newEmailInput}
                          onChange={(e) => setNewEmailInput(e.target.value)}
                          placeholder="Enter new email"
                          className="w-full px-3 py-2 bg-theme-bg border border-theme-border rounded text-theme-text text-sm focus:outline-none focus:border-theme-primary"
                        />
                      </div>
                      <div>
                        <label className="block text-xs text-theme-text-tertiary mb-1">
                          Current Password
                        </label>
                        <input
                          type="password"
                          value={emailPasswordInput}
                          onChange={(e) => setEmailPasswordInput(e.target.value)}
                          placeholder="Confirm your password"
                          className="w-full px-3 py-2 bg-theme-bg border border-theme-border rounded text-theme-text text-sm focus:outline-none focus:border-theme-primary"
                        />
                      </div>
                      {emailChangeError && (
                        <div className="flex items-center gap-1.5 text-xs text-theme-danger">
                          <AlertTriangle className="w-3.5 h-3.5" />
                          <span>{emailChangeError}</span>
                        </div>
                      )}
                      <div className="flex gap-2">
                        <button
                          type="button"
                          onClick={handleEmailChange}
                          disabled={savingEmail || !newEmailInput.trim() || !emailPasswordInput}
                          className="px-3 py-1.5 bg-theme-primary-fill hover:bg-theme-primary-fill-hover disabled:bg-theme-surface disabled:text-theme-text-tertiary disabled:opacity-50 text-theme-on-primary-fill text-sm font-medium rounded transition-colors"
                        >
                          {savingEmail ? 'Sending...' : 'Send Verification'}
                        </button>
                        <button
                          type="button"
                          onClick={() => {
                            setIsEditingEmail(false);
                            setNewEmailInput('');
                            setEmailPasswordInput('');
                            setEmailChangeError(null);
                          }}
                          disabled={savingEmail}
                          className="px-3 py-1.5 bg-theme-bg hover:bg-theme-border text-theme-text-secondary text-sm font-medium rounded transition-colors"
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <p className="text-theme-text-tertiary">Email Status</p>
              <p className={user?.emailVerified ? 'text-theme-success' : 'text-theme-warning'}>
                {user?.emailVerified ? 'Verified' : 'Unverified'}
              </p>
            </div>
            <div>
              <p className="text-theme-text-tertiary">Member Since</p>
              <p className="text-theme-text-secondary">
                {user?.createdAt
                  ? new Date(user.createdAt).toLocaleDateString('en-US', {
                      month: 'short',
                      day: 'numeric',
                      year: 'numeric',
                    })
                  : 'Unknown'}
              </p>
            </div>
            <div>
              <p className="text-theme-text-tertiary">Followers</p>
              <p className="text-theme-text-secondary">{profile?.user.followerCount ?? 0}</p>
            </div>
            <div>
              <p className="text-theme-text-tertiary">Following</p>
              <p className="text-theme-text-secondary">{profile?.user.followingCount ?? 0}</p>
            </div>
          </div>
        </div>
      </section>

      {/* Social Links Section */}
      <section className="mb-6">
        <div className="flex items-center justify-between mb-2">
          <h3 className="text-lg font-medium text-theme-text">Social Links</h3>
        </div>
        <div className="bg-theme-surface rounded-lg p-4 border border-theme-border space-y-3">
          {SOCIAL_PLATFORMS.map((platform) => {
            const Icon = SOCIAL_ICON_MAP[platform.icon];
            const key = platform.key as SocialPlatformKey;
            return (
              <div key={platform.key} className="flex items-center gap-3">
                {Icon && <Icon className="w-4 h-4 text-theme-text-secondary shrink-0" />}
                <label className="text-sm text-theme-text-secondary w-28 shrink-0">
                  {platform.label}
                </label>
                <input
                  type="text"
                  value={socialLinksInput[key] || ''}
                  onChange={(e) =>
                    setSocialLinksInput((prev) => ({
                      ...prev,
                      [key]: e.target.value,
                    }))
                  }
                  placeholder={platform.key === 'discord' ? 'username#1234' : 'username'}
                  className="flex-1 min-w-0 px-3 py-1.5 bg-theme-bg border border-theme-border rounded text-theme-text text-sm focus:outline-none focus:border-theme-primary"
                />
              </div>
            );
          })}
          <button
            type="button"
            onClick={handleSaveSocialLinks}
            disabled={savingSocial}
            className="px-4 py-2 bg-theme-primary-fill hover:bg-theme-primary-fill-hover disabled:opacity-50 text-theme-on-primary-fill text-sm font-medium rounded transition-colors mt-2"
          >
            {savingSocial ? 'Saving...' : 'Save Social Links'}
          </button>
          {socialLinksError && (
            <div className="flex items-center gap-1.5 text-xs text-theme-danger">
              <AlertTriangle className="w-3.5 h-3.5" />
              <span>{socialLinksError}</span>
            </div>
          )}
        </div>
      </section>
    </>
  );
}
